import React, { useEffect, useRef, useState } from 'react';
import { Bell, X, CheckCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';

const POLL_MS = 30000; // refresh count every 30s

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const popoverRef = useRef(null);

  const loadCount = async () => {
    try {
      const r = await api.get('/notifications/unread-count');
      setUnread(r.data?.unread_count || 0);
    } catch {
      /* ignore */
    }
  };

  const loadList = async () => {
    try {
      const r = await api.get('/notifications?limit=20');
      setItems(r.data?.items || []);
      setUnread(r.data?.unread_count || 0);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, POLL_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (open) loadList();
  }, [open]);

  // click outside to close
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markOne = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems(prev => prev.map(it => it.id === id ? { ...it, is_read_by_me: true } : it));
      setUnread((n) => Math.max(0, n - 1));
    } catch {
      /* ignore */
    }
  };

  const markAll = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setItems(prev => prev.map(it => ({ ...it, is_read_by_me: true })));
      setUnread(0);
    } catch {
      /* ignore */
    }
  };

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
        timeZone: 'Europe/Paris',
      });
    } catch {
      return iso;
    }
  };

  const iconFor = (t) => {
    if (t === 'inscription_new') return '📋';
    if (t === 'inscription_validated') return '✅';
    if (t === 'user_register') return '👤';
    if (t === 'contact_new') return '✉️';
    if (t === 'group_full') return '⚠️';
    return '🔔';
  };

  return (
    <div className="relative" ref={popoverRef} data-testid="notif-bell">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-full text-gray-600 hover:text-[#285BBB] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#285BBB] transition-colors"
        aria-label="Notifications"
        data-testid="notif-bell-button"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-[18px] text-center"
            data-testid="notif-bell-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[380px] max-h-[520px] bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
            <h3 className="font-semibold text-[#2C3C65]">Notifications</h3>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={markAll}
                disabled={unread === 0}
                className="text-xs text-[#285BBB] hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed flex items-center gap-1"
                data-testid="notif-mark-all"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Tout lire
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="ml-2 text-gray-400 hover:text-gray-600"
                aria-label="Fermer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1" data-testid="notif-list">
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">
                Aucune notification pour le moment.
              </div>
            ) : (
              items.map((it) => (
                <div
                  key={it.id}
                  className={`flex gap-3 px-4 py-3 border-b border-slate-100 transition-colors hover:bg-slate-50 ${!it.is_read_by_me ? 'bg-blue-50/40' : ''}`}
                  data-testid={`notif-item-${it.id}`}
                >
                  <div className="text-lg flex-shrink-0" aria-hidden="true">
                    {iconFor(it.event_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    {it.link ? (
                      <Link
                        to={it.link}
                        onClick={() => { setOpen(false); markOne(it.id); }}
                        className="block"
                      >
                        <p className={`text-sm leading-snug ${!it.is_read_by_me ? 'font-semibold text-[#2C3C65]' : 'text-[#6777A1]'}`}>
                          {it.title}
                        </p>
                        {it.message && (
                          <p className="text-xs text-[#6777A1] mt-0.5 line-clamp-2">{it.message}</p>
                        )}
                      </Link>
                    ) : (
                      <>
                        <p className={`text-sm leading-snug ${!it.is_read_by_me ? 'font-semibold text-[#2C3C65]' : 'text-[#6777A1]'}`}>
                          {it.title}
                        </p>
                        {it.message && (
                          <p className="text-xs text-[#6777A1] mt-0.5 line-clamp-2">{it.message}</p>
                        )}
                      </>
                    )}
                    <p className="text-[10px] text-gray-400 mt-1">{fmt(it.created_at)}</p>
                  </div>
                  {!it.is_read_by_me && (
                    <button
                      type="button"
                      onClick={() => markOne(it.id)}
                      className="text-[10px] text-[#285BBB] hover:underline self-start"
                      data-testid={`notif-mark-${it.id}`}
                    >
                      Lu
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
